if (!customElements.get('quick-add-modal')) {
  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');

        this.addEventListener('product-info:loaded', ({ target }) => {
          target.addPreProcessCallback(this.preprocessHTML.bind(this));
        });
        this.addEventListener('click', (event) => {
          const link = event.target.closest('.monoree-quick-add-size-guide a[href^="#"]');
          if (!link) return;
          const target = link.closest('.monoree-quick-add-size-guide').querySelector(`#${CSS.escape(link.getAttribute('href').slice(1))}`);
          const scroller = this.querySelector('.quick-add-modal__content');
          if (!target || !scroller) return;
          event.preventDefault();
          scroller.scrollTo({ top: scroller.scrollTop + target.getBoundingClientRect().top - scroller.getBoundingClientRect().top - 64, behavior: 'auto' });
          if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
          target.focus({ preventScroll: true });
        });
      }

      hide(preventFocus = false) {
        this.cancelLoad();
        const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cartNotification) cartNotification.setActiveElement(this.openedBy);
        this.modalContent.innerHTML = '';

        if (preventFocus) this.openedBy = null;
        super.hide();
      }

      connectedCallback() {
        if (this.classList.contains('monoree-quick-add-modal')) {
          document.querySelectorAll(`#${CSS.escape(this.id)}`).forEach((modal) => {
            if (modal !== this) modal.remove();
          });
        }
        super.connectedCallback();
      }

      disconnectedCallback() {
        this.cancelLoad();
        if (this.hasAttribute('open')) super.hide();
      }

      cancelLoad() {
        this.loadRequestId = (this.loadRequestId || 0) + 1;
        this.loadController?.abort();
        clearTimeout(this.loadTimeout);
        this.resetLoading();
      }

      resetLoading() {
        this.loadingOpener?.removeAttribute('aria-disabled');
        this.loadingOpener?.classList.remove('loading');
        this.loadingOpener?.querySelector('.loading__spinner')?.classList.add('hidden');
        this.loadingOpener = null;
        this.loadController = null;
      }

      show(opener) {
        if (this.loadController || this.hasAttribute('open')) return;
        const requestId = this.loadRequestId = (this.loadRequestId || 0) + 1;
        const controller = this.loadController = new AbortController();
        this.loadingOpener = opener;
        this.loadTimeout = setTimeout(() => controller.abort(), 15000);
        opener.setAttribute('aria-disabled', true);
        opener.classList.add('loading');
        opener.querySelector('.loading__spinner').classList.remove('hidden');

        fetch(opener.getAttribute('data-product-url'), { signal: controller.signal })
          .then((response) => {
            if (!response.ok) throw new Error('Product could not be loaded');
            return response.text();
          })
          .then((responseText) => {
            if (requestId !== this.loadRequestId || controller.signal.aborted || !this.isConnected) return;
            const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
            const productElement = responseHTML.querySelector('product-info');
            if (!productElement) throw new Error('Product information is missing');

            this.preprocessHTML(productElement);
            HTMLUpdateUtility.setInnerHTML(this.modalContent, productElement.outerHTML);

            if (window.Shopify && Shopify.PaymentButton) {
              Shopify.PaymentButton.init();
            }
            if (window.ProductModel) window.ProductModel.loadShopifyXR();

            this.modalContent.querySelector('product-component')?.dispatchViewEvent?.();

            super.show(opener);
          })
          .catch((error) => {
            if (requestId !== this.loadRequestId || !this.isConnected) return;
            if (this.classList.contains('monoree-quick-add-modal')) {
              window.location.assign(opener.getAttribute('data-product-url'));
              return;
            }
            console.error(error);
          })
          .finally(() => {
            if (requestId !== this.loadRequestId) return;
            clearTimeout(this.loadTimeout);
            this.resetLoading();
          });
      }

      preprocessHTML(productElement) {
        productElement.classList.forEach((classApplied) => {
          if (classApplied.startsWith('color-') || classApplied === 'gradient')
            this.modalContent.classList.add(classApplied);
        });
        this.preventDuplicatedIDs(productElement);
        if (this.classList.contains('monoree-quick-add-modal')) this.prepareSizeGuide(productElement);
        this.removeDOMElements(productElement);
        this.removeGalleryListSemantic(productElement);
        this.updateImageSizes(productElement);
        this.preventVariantURLSwitching(productElement);
        if (this.classList.contains('monoree-quick-add-modal')) this.compactProduct(productElement);
        this.preventDuplicatedStylesheets(productElement);
      }

      preventDuplicatedStylesheets(productElement) {
        const stylesheetKey = (link) => `${new URL(link.getAttribute('href'), document.baseURI).href}|${link.media || 'all'}`;
        const existing = new Set(
          Array.from(document.querySelectorAll('link[rel="stylesheet"][href]'))
            .filter((link) => !this.modalContent.contains(link))
            .map(stylesheetKey)
        );
        productElement.querySelectorAll('link[rel="stylesheet"][href]').forEach((link) => {
          const key = stylesheetKey(link);
          if (existing.has(key)) link.remove();
          else existing.add(key);
        });
      }

      compactProduct(productElement) {
        productElement.querySelectorAll('.product__media img').forEach((image) => image.setAttribute('sizes', '88px'));
        productElement.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.remove('scroll-trigger', 'animate--fade-in', 'animate--slide-in', 'scroll-trigger--offscreen'));
        productElement.querySelectorAll('.monoree-breadcrumbs, [data-monoree-accelerated], [data-monoree-restock], .monoree-sticky-atc').forEach((element) => element.remove());
        const info = productElement.querySelector('.product__info-container');
        if (!info) return;
        const keep = '.product__title, [id^="price-"], variant-selects, .product__inventory, .product-form__quantity, .product__view-details, [id^="Volume-"], [id^="Quantity-Rules-"]';
        Array.from(info.children).forEach((child) => {
          if (!child.matches(keep) && !child.querySelector('product-form')) child.remove();
        });
        productElement.querySelector('.product-form__submit')?.classList.remove('button--secondary');
      }

      prepareSizeGuide(productElement) {
        productElement.querySelectorAll('.monoree-size-guide-row').forEach((row, index) => {
          const opener = row.querySelector('[data-monoree-size-guide],a[href]');
          if (!opener) return;
          const modalId = opener.dataset.monoreeSizeGuide;
          const modal = (modalId && (productElement.querySelector(`#${CSS.escape(modalId)}`) || productElement.ownerDocument.getElementById(modalId))) || productElement.querySelector('.monoree-size-guide-modal') || productElement.ownerDocument.querySelector('.monoree-size-guide-modal');
          const source = modal?.querySelector('.product-popup-modal__content-info');
          if (!source) {
            if (modalId) row.remove();
            return;
          }
          this.sizeGuideSequence = (this.sizeGuideSequence || 0) + 1;
          const namespace = `${this.id}-size-guide-${this.sizeGuideSequence}-${index}`;
          const content = source.cloneNode(true);
          content.className = 'monoree-quick-add-size-guide__content';
          content.id = `${namespace}-content`;
          content.querySelectorAll('script').forEach((script) => script.remove());
          const ids = new Map();
          content.querySelectorAll('[id]').forEach((element) => {
            const id = `${namespace}-${element.id}`;
            ids.set(element.id, id);
            element.id = id;
          });
          content.querySelectorAll('a[href^="#"]').forEach((link) => {
            const id = ids.get(link.getAttribute('href').slice(1));
            if (id) link.setAttribute('href', `#${id}`);
          });
          content.querySelectorAll('[for], [aria-labelledby], [aria-describedby], [aria-controls], [headers]').forEach((element) => {
            ['for', 'aria-labelledby', 'aria-describedby', 'aria-controls', 'headers'].forEach((name) => {
              if (!element.hasAttribute(name)) return;
              element.setAttribute(name, element.getAttribute(name).split(/\s+/).map((id) => ids.get(id) || id).join(' '));
            });
          });
          const details = productElement.ownerDocument.createElement('details');
          details.className = 'monoree-quick-add-size-guide';
          details.open = productElement.matches('variant-selects') && Boolean(this.modalContent.querySelector('.monoree-quick-add-size-guide[open]'));
          const summary = productElement.ownerDocument.createElement('summary');
          summary.className = 'link underlined-link';
          summary.textContent = opener.textContent.trim();
          summary.setAttribute('aria-controls', content.id);
          const indicator = productElement.ownerDocument.createElement('span');
          indicator.className = 'monoree-quick-add-size-guide__indicator';
          indicator.setAttribute('aria-hidden', 'true');
          summary.appendChild(indicator);
          details.append(summary, content);
          row.replaceChildren(details);
        });
      }

      preventVariantURLSwitching(productElement) {
        productElement.setAttribute('data-update-url', 'false');
      }

      removeDOMElements(productElement) {
        productElement.querySelectorAll('script[type="application/ld+json"]').forEach((schema) => schema.remove());
        const pickupAvailability = productElement.querySelector('pickup-availability');
        if (pickupAvailability) pickupAvailability.remove();

        const productModal = productElement.querySelector('product-modal');
        if (productModal) productModal.remove();

        const modalDialog = productElement.querySelectorAll('modal-dialog');
        if (modalDialog) modalDialog.forEach((modal) => modal.remove());
      }

      preventDuplicatedIDs(productElement) {
        const sectionId = productElement.dataset.section;

        const oldId = sectionId;
        const newId = `quickadd-${sectionId}`;
        productElement.innerHTML = productElement.innerHTML.replaceAll(oldId, newId);
        Array.from(productElement.attributes).forEach((attribute) => {
          if (attribute.value.includes(oldId)) {
            productElement.setAttribute(attribute.name, attribute.value.replace(oldId, newId));
          }
        });

        productElement.dataset.originalSection = sectionId;
      }

      removeGalleryListSemantic(productElement) {
        const galleryList = productElement.querySelector('[id^="Slider-Gallery"]');
        if (!galleryList) return;

        galleryList.setAttribute('role', 'presentation');
        galleryList.querySelectorAll('[id^="Slide-"]').forEach((li) => li.setAttribute('role', 'presentation'));
      }

      updateImageSizes(productElement) {
        const product = productElement.querySelector('.product');
        const desktopColumns = product?.classList.contains('product--columns');
        if (!desktopColumns) return;

        const mediaImages = product.querySelectorAll('.product__media img');
        if (!mediaImages.length) return;

        let mediaImageSizes =
          '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

        if (product.classList.contains('product--medium')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '605px');
        } else if (product.classList.contains('product--small')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '495px');
        }

        mediaImages.forEach((img) => img.setAttribute('sizes', mediaImageSizes));
      }
    }
  );
}
